import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { HiOutlineUser, HiOutlineMail, HiOutlineLogout, HiOutlineShoppingBag, HiOutlineHeart, HiOutlineCog, HiOutlineKey } from 'react-icons/hi';

const Account = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: profileData.phone,
      });
      toast.success(t('messages.profileUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('messages.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t('messages.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await updateProfile({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success(t('messages.success'));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success(t('messages.logoutSuccess'));
    navigate('/');
  };

  const menuItems = [
    { id: 'profile', label: t('account.personalInfo'), icon: HiOutlineUser },
    { id: 'password', label: t('account.changePassword'), icon: HiOutlineKey },
  ];

  const quickLinks = [
    { to: '/orders', label: t('account.recentOrders'), icon: HiOutlineShoppingBag, desc: t('account.ordersDesc') },
    { to: '/wishlist', label: t('nav.wishlist'), icon: HiOutlineHeart, desc: t('account.wishlistDesc') },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="bg-primary-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">{t('account.myAccount')}</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-luxury-gold transition-colors">{t('nav.home')}</Link>
            <span>/</span>
            <span>{t('account.myAccount')}</span>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Quick Links */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {quickLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-4 p-6 bg-white border border-gray-100 hover:border-luxury-gold transition-colors"
              >
                <link.icon className="w-8 h-8 text-gray-400" />
                <div>
                  <h3 className="font-medium">{link.label}</h3>
                  <p className="text-sm text-gray-500">{link.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="lg:flex lg:gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 mb-8 lg:mb-0">
              {/* User Info */}
              <div className="bg-white p-6 mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="font-serif text-xl">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium">{user?.firstName} {user?.lastName}</h3>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Menu */}
              <nav className="bg-white">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-6 py-4 text-left border-l-2 transition-colors ${
                      activeTab === item.id 
                        ? 'border-luxury-gold bg-primary-50 text-luxury-charcoal' 
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                ))}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-6 py-4 text-left border-l-2 border-transparent hover:bg-red-50 text-red-600"
                >
                  <HiOutlineLogout className="w-5 h-5" />
                  {t('nav.logout')}
                </button>
              </nav>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {activeTab === 'profile' && (
                <div className="bg-white p-6 lg:p-8">
                  <h2 className="font-serif text-2xl mb-6">{t('account.personalInfo')}</h2>
                  <form onSubmit={handleProfileSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm mb-2">{t('auth.firstName')}</label>
                        <input
                          type="text"
                          name="firstName"
                          value={profileData.firstName}
                          onChange={handleProfileChange}
                          className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                        />
                      </div>
                      <div>
                        <label className="block text-sm mb-2">{t('auth.lastName')}</label>
                        <input
                          type="text"
                          name="lastName"
                          value={profileData.lastName}
                          onChange={handleProfileChange}
                          className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm mb-2">{t('auth.email')}</label>
                      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200">
                        <HiOutlineMail className="w-5 h-5 text-gray-400" />
                        <span className="text-gray-600">{profileData.email}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{t('account.emailCannotChange')}</p>
                    </div>

                    <div>
                      <label className="block text-sm mb-2">{t('checkout.phone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleProfileChange}
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                        placeholder={t('account.enterPhone')}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-luxury-charcoal text-white px-8 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      {loading ? t('common.saving') : t('account.saveChanges')}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'password' && (
                <div className="bg-white p-6 lg:p-8">
                  <h2 className="font-serif text-2xl mb-6">{t('account.changePassword')}</h2>
                  <form onSubmit={handlePasswordSubmit} className="space-y-6 max-w-md">
                    <div>
                      <label className="block text-sm mb-2">{t('account.currentPassword')}</label>
                      <input
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>

                    <div>
                      <label className="block text-sm mb-2">{t('account.newPassword')}</label>
                      <input
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('account.passwordMinChars')}</p>
                    </div>

                    <div>
                      <label className="block text-sm mb-2">{t('account.confirmPassword')}</label>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 focus:outline-none focus:border-luxury-gold"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-luxury-charcoal text-white px-8 py-3 text-sm uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      {loading ? t('common.updating') : t('account.updatePassword')}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Account;
